import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FiPlus, FiEdit3, FiTrash2, FiChevronUp, FiChevronDown, FiSave, FiCheck, FiX, FiGrid, FiExternalLink, FiChevronRight } from 'react-icons/fi';
import apiClient from '../utils/apiClient';
import { useToast } from '../context/ToastContext';
import '../styles/Admin.css';

const AdminCategories = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const [sections, setSections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [editingSection, setEditingSection] = useState(null);
    const [newSection, setNewSection] = useState({ id: '', name: '' });

    // Category config state
    const [expandedCategory, setExpandedCategory] = useState(null);
    const [categoryConfigs, setCategoryConfigs] = useState({});
    const [savingConfig, setSavingConfig] = useState(false);

    // Trending products state
    const [trendingSearch, setTrendingSearch] = useState('');
    const [trendingSearchResults, setTrendingSearchResults] = useState([]);
    const [searchingProducts, setSearchingProducts] = useState(false);

    useEffect(() => {
        const token = sessionStorage.getItem('adminToken');
        if (!token) {
            navigate('/admin/login');
            return;
        }
        fetchSections();
    }, [navigate]);

    const fetchSections = async () => {
        try {
            setLoading(true);
            const response = await apiClient.get('/trending/sections');
            // Filter out 'featured' as it's not a navbar category
            const navbarCategories = (response.data.sections || []).filter(s => s.id !== 'featured');
            setSections(navbarCategories);
        } catch (error) {
            console.error('Error fetching sections:', error);
            toast.error('Failed to load categories');
        } finally {
            setLoading(false);
        }
    };

    // Default category configurations (fallback when no config in database)
    const defaultCategoryConfigs = {
        stationary: {
            title: 'Stationary',
            subtitle: 'Premium Office & Art Supplies',
            bannerImage: 'https://images.unsplash.com/photo-1584628804572-f84284d9f388?q=80&w=1170&auto=format&fit=crop',
            subCategories: [
                { name: 'Notebooks', image: 'https://plus.unsplash.com/premium_photo-1667251758255-514ef43844fb?q=80&w=687&auto=format&fit=crop', filter: 'notebooks' },
                { name: 'Pens & Pencils', image: 'https://images.unsplash.com/photo-1585336261022-680e295ce3fe?q=80&w=300&auto=format&fit=crop', filter: 'pens' },
                { name: 'Art Supplies', image: 'https://images.unsplash.com/photo-1513364776144-60967b0f800f?q=80&w=300&auto=format&fit=crop', filter: 'art' },
                { name: 'Desk Organizers', image: 'https://images.unsplash.com/photo-1644463589256-02679b9c0767?q=80&w=880&auto=format&fit=crop', filter: 'organizers' },
                { name: 'Gift Wrapping', image: 'https://images.unsplash.com/photo-1513885535751-8b9238bd345a?q=80&w=300&auto=format&fit=crop', filter: 'wrapping' },
            ]
        },
        textiles: {
            title: 'Textiles & Fabrics',
            subtitle: 'Authentic Handloom & Fabrics',
            bannerImage: 'https://images.unsplash.com/photo-1722694583723-b75de0918c44?q=80&w=1170&auto=format&fit=crop',
            subCategories: [
                { name: 'Sarees', image: 'https://images.unsplash.com/photo-1727430228383-aa1fb59db8bf?w=600&auto=format&fit=crop&q=60', filter: 'saree' },
                { name: 'Kurtas', image: 'https://images.unsplash.com/photo-1731577506253-0fd9cd00ab7f?q=80&w=687&auto=format&fit=crop', filter: 'kurta' },
                { name: 'Fabrics', image: 'https://images.unsplash.com/photo-1601056639638-c53c50e13ead?w=600&auto=format&fit=crop&q=60', filter: 'fabric' },
                { name: 'Dupattas', image: 'https://images.unsplash.com/photo-1680506660555-1c225f5da953?q=80&w=764&auto=format&fit=crop', filter: 'dupatta' },
            ]
        },
        pottery: {
            title: 'Pottery & Ceramics',
            subtitle: 'Handcrafted Clay & Ceramic Art',
            bannerImage: 'https://images.unsplash.com/photo-1493106641515-6b5631de4bb9?q=80&w=1920&auto=format&fit=crop',
            subCategories: [
                { name: 'Vases', image: 'https://images.unsplash.com/photo-1597696929736-6d13bed8e6a8?w=600&auto=format&fit=crop&q=60', filter: 'vase' },
                { name: 'Dinnerware', image: 'https://images.unsplash.com/photo-1594404895374-f89ce340ae38?w=600&auto=format&fit=crop&q=60', filter: 'dinnerware' },
                { name: 'Planters', image: 'https://images.unsplash.com/photo-1485955900006-10f4d324d411?q=80&w=300&auto=format&fit=crop', filter: 'planter' },
                { name: 'Mugs', image: 'https://plus.unsplash.com/premium_photo-1719289718424-0f5071da5a3a?w=600&auto=format&fit=crop&q=60', filter: 'mug' },
            ]
        },
        handicrafts: {
            title: 'Handicrafts',
            subtitle: 'Traditional Indian Craftsmanship',
            bannerImage: 'https://images.unsplash.com/photo-1652355056810-ce141db641d9?q=80&w=1170&auto=format&fit=crop',
            subCategories: [
                { name: 'Wooden Crafts', image: 'https://plus.unsplash.com/premium_photo-1678752717582-f46bb39890c4?q=80&w=1170&auto=format&fit=crop', filter: 'wood' },
                { name: 'Metal Art', image: 'https://images.unsplash.com/photo-1652164726284-2e82259cddfc?q=80&w=1170&auto=format&fit=crop', filter: 'metal' },
                { name: 'Paintings', image: 'https://images.unsplash.com/photo-1579783902614-a3fb3927b6a5?q=80&w=300&auto=format&fit=crop', filter: 'painting' },
                { name: 'Masks', image: 'https://images.unsplash.com/photo-1745429806387-6224c9940799?w=600&auto=format&fit=crop&q=60', filter: 'mask' },
            ]
        },
        electronics: {
            title: 'Electronics',
            subtitle: 'Modern Gadgets & Accessories',
            bannerImage: 'https://images.unsplash.com/photo-1550009158-9ebf69173e03?q=80&w=1920&auto=format&fit=crop',
            subCategories: [
                { name: 'Audio', image: 'https://images.unsplash.com/photo-1583394838336-acd977736f90?w=600&auto=format&fit=crop&q=60', filter: 'audio' },
                { name: 'Accessories', image: 'https://images.unsplash.com/photo-1693279504914-d08266ecbe66?q=80&w=880&auto=format&fit=crop', filter: 'accessories' },
                { name: 'Smart Home', image: 'https://images.unsplash.com/photo-1730967844913-29eb5cae5f34?q=80&w=1170&auto=format&fit=crop', filter: 'smart' },
            ]
        },
        'home-decor': {
            title: 'Home Decor',
            subtitle: 'Beautify Your Living Space',
            bannerImage: 'https://images.unsplash.com/photo-1513519245088-0e12902e5a38?q=80&w=1920&auto=format&fit=crop',
            subCategories: [
                { name: 'Wall Art', image: 'https://images.unsplash.com/photo-1452457005517-a0dd81caca2a?q=80&w=1095&auto=format&fit=crop', filter: 'wall' },
                { name: 'Lighting', image: 'https://images.unsplash.com/photo-1540932239986-30128078f3c5?w=600&auto=format&fit=crop&q=60', filter: 'light' },
                { name: 'Cushions', image: 'https://plus.unsplash.com/premium_photo-1764329347430-7330d2e62713?w=600&auto=format&fit=crop&q=60', filter: 'cushion' },
                { name: 'Rugs', image: 'https://plus.unsplash.com/premium_photo-1725621529322-7215550871ce?q=80&w=1169&auto=format&fit=crop', filter: 'rug' },
            ]
        }
    };

    const fetchCategoryConfig = async (categoryId) => {
        try {
            const response = await apiClient.get(`/trending/category-config/${categoryId}`);
            if (response.data.config) {
                setCategoryConfigs(prev => ({
                    ...prev,
                    [categoryId]: response.data.config
                }));
            } else {
                // Use default config if available, otherwise empty
                const defaultConfig = defaultCategoryConfigs[categoryId] || {
                    title: sections.find(s => s.id === categoryId)?.name || categoryId,
                    subtitle: '',
                    bannerImage: '',
                    subCategories: []
                };
                setCategoryConfigs(prev => ({
                    ...prev,
                    [categoryId]: defaultConfig
                }));
            }
        } catch (error) {
            console.error('Error fetching category config:', error);
            // On error, also use defaults
            const defaultConfig = defaultCategoryConfigs[categoryId] || {
                title: sections.find(s => s.id === categoryId)?.name || categoryId,
                subtitle: '',
                bannerImage: '',
                subCategories: []
            };
            setCategoryConfigs(prev => ({
                ...prev,
                [categoryId]: defaultConfig
            }));
        }
    };

    const getAuthHeaders = () => ({
        headers: { Authorization: `Bearer ${sessionStorage.getItem('adminToken')}` }
    });

    const handleAddSection = async () => {
        if (!newSection.id.trim() || !newSection.name.trim()) {
            toast.error('Please provide both ID and name for the new category');
            return;
        }

        const sectionId = newSection.id.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-');

        if (sections.some(s => s.id === sectionId)) {
            toast.error('A category with this ID already exists');
            return;
        }

        try {
            setSaving(true);
            const response = await apiClient.post('/trending/sections', {
                id: sectionId,
                name: newSection.name
            }, getAuthHeaders());
            // Filter out featured from response
            const navbarCategories = (response.data.sections || []).filter(s => s.id !== 'featured');
            setSections(navbarCategories);
            setNewSection({ id: '', name: '' });
            toast.success('Category added successfully!');
        } catch (error) {
            console.error('Error adding section:', error);
            toast.error(error.response?.data?.error || 'Failed to add category');
        } finally {
            setSaving(false);
        }
    };

    const handleUpdateSectionName = async (sectionId, newName) => {
        try {
            setSaving(true);
            const response = await apiClient.put(`/trending/sections/${sectionId}`, {
                name: newName
            }, getAuthHeaders());
            // Filter out featured from response
            const navbarCategories = (response.data.sections || []).filter(s => s.id !== 'featured');
            setSections(navbarCategories);
            setEditingSection(null);
            toast.success('Category renamed successfully!');
        } catch (error) {
            console.error('Error updating section:', error);
            toast.error(error.response?.data?.error || 'Failed to update category');
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteSection = async (sectionId) => {
        if (!window.confirm(`Are you sure you want to delete this category? This action cannot be undone.`)) {
            return;
        }

        try {
            setSaving(true);
            const response = await apiClient.delete(`/trending/sections/${sectionId}`, getAuthHeaders());
            // Filter out featured from response
            const navbarCategories = (response.data.sections || []).filter(s => s.id !== 'featured');
            setSections(navbarCategories);
            toast.success('Category deleted successfully!');
        } catch (error) {
            console.error('Error deleting section:', error);
            toast.error(error.response?.data?.error || 'Failed to delete category');
        } finally {
            setSaving(false);
        }
    };

    const moveSectionInList = (sectionId, direction) => {
        const index = sections.findIndex(s => s.id === sectionId);
        if (index === -1) return;

        if (direction === 'up' && index <= 0) return;
        if (direction === 'down' && index >= sections.length - 1) return;

        const newSections = [...sections];
        const newIndex = direction === 'up' ? index - 1 : index + 1;
        [newSections[index], newSections[newIndex]] = [newSections[newIndex], newSections[index]];
        setSections(newSections);
    };

    const handleSaveSectionOrder = async () => {
        try {
            setSaving(true);
            // Add featured back when saving to preserve it in backend
            const allSections = [{ id: 'featured', name: 'Featured Products', isDefault: true }, ...sections];
            await apiClient.put('/trending/sections', { sections: allSections }, getAuthHeaders());
            toast.success('Category order saved successfully!');
        } catch (error) {
            console.error('Error saving section order:', error);
            toast.error('Failed to save category order');
        } finally {
            setSaving(false);
        }
    };

    const toggleCategoryConfig = (categoryId) => {
        if (expandedCategory === categoryId) {
            setExpandedCategory(null);
        } else {
            setExpandedCategory(categoryId);
            if (!categoryConfigs[categoryId]) {
                fetchCategoryConfig(categoryId);
            }
        }
    };

    const updateCategoryConfigField = (categoryId, field, value) => {
        setCategoryConfigs(prev => ({
            ...prev,
            [categoryId]: {
                ...prev[categoryId],
                [field]: value
            }
        }));
    };

    const addSubCategory = (categoryId) => {
        const config = categoryConfigs[categoryId] || { subCategories: [] };
        const newSubCategories = [...(config.subCategories || []), { name: '', image: '', filter: '' }];
        updateCategoryConfigField(categoryId, 'subCategories', newSubCategories);

        // Scroll to the newly added subcategory after state update
        setTimeout(() => {
            const subcategoryItems = document.querySelectorAll('.subcategory-item');
            if (subcategoryItems.length > 0) {
                const lastItem = subcategoryItems[subcategoryItems.length - 1];
                lastItem.scrollIntoView({ behavior: 'smooth', block: 'center' });
                // Focus on the first input of the new subcategory
                const firstInput = lastItem.querySelector('input');
                if (firstInput) firstInput.focus();
            }
        }, 100);
    };

    const updateSubCategory = (categoryId, index, field, value) => {
        const config = categoryConfigs[categoryId] || { subCategories: [] };
        const newSubCategories = [...(config.subCategories || [])];
        newSubCategories[index] = { ...newSubCategories[index], [field]: value };
        updateCategoryConfigField(categoryId, 'subCategories', newSubCategories);
    };

    const removeSubCategory = (categoryId, index) => {
        const config = categoryConfigs[categoryId] || { subCategories: [] };
        const newSubCategories = config.subCategories.filter((_, i) => i !== index);
        updateCategoryConfigField(categoryId, 'subCategories', newSubCategories);
    };

    const moveSubCategory = (categoryId, index, direction) => {
        const config = categoryConfigs[categoryId] || { subCategories: [] };
        const newSubCategories = [...(config.subCategories || [])];
        const newIndex = direction === 'up' ? index - 1 : index + 1;
        if (newIndex < 0 || newIndex >= newSubCategories.length) return;
        [newSubCategories[index], newSubCategories[newIndex]] = [newSubCategories[newIndex], newSubCategories[index]];
        updateCategoryConfigField(categoryId, 'subCategories', newSubCategories);
    };

    // Trending products functions
    const searchProducts = async (query, categoryId) => {
        if (!query.trim()) {
            setTrendingSearchResults([]);
            return;
        }
        try {
            setSearchingProducts(true);
            const response = await apiClient.get(`/products?search=${encodeURIComponent(query)}&category=${categoryId}&limit=10`);
            const products = response.data.products || [];
            // Filter out products already in trending
            const currentTrending = categoryConfigs[categoryId]?.trendingProducts || [];
            const filtered = products.filter(p => !currentTrending.some(t => t.id === p.id));
            setTrendingSearchResults(filtered);
        } catch (error) {
            console.error('Error searching products:', error);
        } finally {
            setSearchingProducts(false);
        }
    };

    const addTrendingProduct = (categoryId, product) => {
        const config = categoryConfigs[categoryId] || {};
        const currentTrending = config.trendingProducts || [];
        if (currentTrending.length >= 8) {
            toast.error('Maximum 8 trending products allowed');
            return;
        }
        const newTrending = [...currentTrending, { id: product.id, name: product.name, image: product.images?.[0] || '' }];
        updateCategoryConfigField(categoryId, 'trendingProducts', newTrending);
        setTrendingSearchResults(prev => prev.filter(p => p.id !== product.id));
        setTrendingSearch('');
    };

    const removeTrendingProduct = (categoryId, productId) => {
        const config = categoryConfigs[categoryId] || {};
        const newTrending = (config.trendingProducts || []).filter(p => p.id !== productId);
        updateCategoryConfigField(categoryId, 'trendingProducts', newTrending);
    };

    const moveTrendingProduct = (categoryId, index, direction) => {
        const config = categoryConfigs[categoryId] || {};
        const trending = [...(config.trendingProducts || [])];
        const newIndex = direction === 'up' ? index - 1 : index + 1;
        if (newIndex < 0 || newIndex >= trending.length) return;
        [trending[index], trending[newIndex]] = [trending[newIndex], trending[index]];
        updateCategoryConfigField(categoryId, 'trendingProducts', trending);
    };

    const handleSaveCategoryConfig = async (categoryId) => {
        try {
            setSavingConfig(true);
            const config = categoryConfigs[categoryId];
            await apiClient.put(`/trending/category-config/${categoryId}`, config, getAuthHeaders());
            toast.success('Category page configuration saved!');
        } catch (error) {
            console.error('Error saving category config:', error);
            toast.error('Failed to save category configuration');
        } finally {
            setSavingConfig(false);
        }
    };

    if (loading) {
        return (
            <div className="admin-container">
                <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
                    Loading categories...
                </div>
            </div>
        );
    }

    return (
        <div className="admin-container">
            <div className="page-header">
                <div className="header-content">
                    <div className="header-icon">
                        <FiGrid size={28} />
                    </div>
                    <div className="header-text">
                        <h1>Shop Categories</h1>
                        <p>Manage navbar categories, subcategories, and shop page configurations</p>
                    </div>
                </div>
            </div>

            <div className="categories-manager">
                {/* Add New Category */}
                <div className="add-category-card">
                    <h3>Add New Category</h3>
                    <div className="add-category-form">
                        <div className="form-group">
                            <label>Category ID</label>
                            <input
                                type="text"
                                placeholder="e.g., jewelry (lowercase, no spaces)"
                                value={newSection.id}
                                onChange={(e) => setNewSection({ ...newSection, id: e.target.value })}
                            />
                            <span className="form-hint">Used in URLs</span>
                        </div>
                        <div className="form-group">
                            <label>Display Name</label>
                            <input
                                type="text"
                                placeholder="e.g., Jewelry & Accessories"
                                value={newSection.name}
                                onChange={(e) => setNewSection({ ...newSection, name: e.target.value })}
                            />
                            <span className="form-hint">Shown in navigation</span>
                        </div>
                        <button
                            className="add-btn"
                            onClick={handleAddSection}
                            disabled={saving}
                        >
                            <FiPlus /> Add Category
                        </button>
                    </div>
                </div>

                {/* Categories List */}
                <div className="categories-list-card">
                    <div className="card-header">
                        <h3>Navbar Categories ({sections.length})</h3>
                        <button
                            className="save-order-btn"
                            onClick={handleSaveSectionOrder}
                            disabled={saving}
                        >
                            <FiSave /> {saving ? 'Saving...' : 'Save Order'}
                        </button>
                    </div>

                    <div className="categories-list">
                        {sections.map((section, index) => (
                            <div key={section.id} className="category-wrapper">
                                <div className={`category-item ${expandedCategory === section.id ? 'expanded' : ''}`}>
                                    <div className="category-order">
                                        <span className="order-number">{index + 1}</span>
                                        <div className="order-buttons">
                                            <button
                                                onClick={() => moveSectionInList(section.id, 'up')}
                                                disabled={index === 0}
                                                title="Move up"
                                            >
                                                <FiChevronUp size={16} />
                                            </button>
                                            <button
                                                onClick={() => moveSectionInList(section.id, 'down')}
                                                disabled={index >= sections.length - 1}
                                                title="Move down"
                                            >
                                                <FiChevronDown size={16} />
                                            </button>
                                        </div>
                                    </div>

                                    <div className="category-info" onClick={() => toggleCategoryConfig(section.id)}>
                                        {editingSection === section.id ? (
                                            <input
                                                type="text"
                                                defaultValue={section.name}
                                                className="edit-input"
                                                autoFocus
                                                onClick={(e) => e.stopPropagation()}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        handleUpdateSectionName(section.id, e.target.value);
                                                    } else if (e.key === 'Escape') {
                                                        setEditingSection(null);
                                                    }
                                                }}
                                            />
                                        ) : (
                                            <>
                                                <span className="category-name">{section.name}</span>
                                                <span className="category-id">ID: {section.id}</span>
                                            </>
                                        )}
                                        <span className="expand-hint">
                                            <FiChevronRight className={expandedCategory === section.id ? 'rotated' : ''} />
                                            Click to configure shop page
                                        </span>
                                    </div>

                                    <div className="category-actions">
                                        {editingSection === section.id ? (
                                            <>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        const input = e.target.closest('.category-item').querySelector('.edit-input');
                                                        handleUpdateSectionName(section.id, input.value);
                                                    }}
                                                    className="action-btn save"
                                                    title="Save"
                                                >
                                                    <FiCheck size={22} />
                                                </button>
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); setEditingSection(null); }}
                                                    className="action-btn cancel"
                                                    title="Cancel"
                                                >
                                                    <FiX size={22} />
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <Link
                                                    to={`/shop/${section.id}`}
                                                    target="_blank"
                                                    className="action-btn view"
                                                    title="View shop page"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <FiExternalLink size={22} />
                                                </Link>
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); setEditingSection(section.id); }}
                                                    className="action-btn edit"
                                                    title="Rename"
                                                >
                                                    <FiEdit3 size={22} />
                                                </button>
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); handleDeleteSection(section.id); }}
                                                    className="action-btn delete"
                                                    title="Delete"
                                                >
                                                    <FiTrash2 size={22} />
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>

                                {/* Expanded Config Panel */}
                                {expandedCategory === section.id && (
                                    <div className="category-config-panel">
                                        {categoryConfigs[section.id] ? (
                                            <>
                                                <div className="config-section">
                                                    <h4>Shop Page Settings</h4>
                                                    <div className="config-form">
                                                        <div className="config-field">
                                                            <label>Subtitle</label>
                                                            <input
                                                                type="text"
                                                                placeholder="e.g., Premium Office & Art Supplies"
                                                                value={categoryConfigs[section.id]?.subtitle || ''}
                                                                onChange={(e) => updateCategoryConfigField(section.id, 'subtitle', e.target.value)}
                                                            />
                                                        </div>
                                                        <div className="config-field">
                                                            <label>Banner Image URL</label>
                                                            <input
                                                                type="text"
                                                                placeholder="https://images.unsplash.com/..."
                                                                value={categoryConfigs[section.id]?.bannerImage || ''}
                                                                onChange={(e) => updateCategoryConfigField(section.id, 'bannerImage', e.target.value)}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="config-section">
                                                    <div className="section-header">
                                                        <h4>Subcategories</h4>
                                                        <button className="add-sub-btn" onClick={() => addSubCategory(section.id)}>
                                                            <FiPlus /> Add Subcategory
                                                        </button>
                                                    </div>

                                                    <div className="subcategories-list">
                                                        {(categoryConfigs[section.id]?.subCategories || []).map((sub, subIndex) => (
                                                            <div key={subIndex} className="subcategory-item">
                                                                <div className="sub-order">
                                                                    <button
                                                                        onClick={() => moveSubCategory(section.id, subIndex, 'up')}
                                                                        disabled={subIndex === 0}
                                                                    >
                                                                        <FiChevronUp size={14} />
                                                                    </button>
                                                                    <button
                                                                        onClick={() => moveSubCategory(section.id, subIndex, 'down')}
                                                                        disabled={subIndex === (categoryConfigs[section.id]?.subCategories?.length || 0) - 1}
                                                                    >
                                                                        <FiChevronDown size={14} />
                                                                    </button>
                                                                </div>
                                                                <div className="sub-fields">
                                                                    <input
                                                                        type="text"
                                                                        placeholder="Name (e.g., Notebooks)"
                                                                        value={sub.name || ''}
                                                                        onChange={(e) => updateSubCategory(section.id, subIndex, 'name', e.target.value)}
                                                                    />
                                                                    <input
                                                                        type="text"
                                                                        placeholder="Image URL"
                                                                        value={sub.image || ''}
                                                                        onChange={(e) => updateSubCategory(section.id, subIndex, 'image', e.target.value)}
                                                                    />
                                                                    <input
                                                                        type="text"
                                                                        placeholder="Filter keyword (e.g., notebooks)"
                                                                        value={sub.filter || ''}
                                                                        onChange={(e) => updateSubCategory(section.id, subIndex, 'filter', e.target.value)}
                                                                    />
                                                                </div>
                                                                <button
                                                                    className="remove-sub-btn"
                                                                    onClick={() => removeSubCategory(section.id, subIndex)}
                                                                >
                                                                    <FiTrash2 size={14} />
                                                                </button>
                                                            </div>
                                                        ))}
                                                        {(!categoryConfigs[section.id]?.subCategories || categoryConfigs[section.id].subCategories.length === 0) && (
                                                            <div className="no-subcategories">
                                                                No subcategories yet. Click "Add Subcategory" to create one.
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Trending Products Section */}
                                                <div className="config-section">
                                                    <div className="section-header">
                                                        <h4>Trending Products (Shown on Shop Page)</h4>
                                                        <span className="trending-count">
                                                            {categoryConfigs[section.id]?.trendingProducts?.length || 0}/8
                                                        </span>
                                                    </div>

                                                    <div className="trending-search">
                                                        <input
                                                            type="text"
                                                            placeholder="Search products to add..."
                                                            value={trendingSearch}
                                                            onChange={(e) => {
                                                                setTrendingSearch(e.target.value);
                                                                searchProducts(e.target.value, section.id);
                                                            }}
                                                        />
                                                        {searchingProducts && <span className="searching">Searching...</span>}
                                                    </div>

                                                    {trendingSearchResults.length > 0 && (
                                                        <div className="search-results">
                                                            {trendingSearchResults.map(product => (
                                                                <div key={product.id} className="search-result-item">
                                                                    <img src={product.images?.[0] || '/placeholder.png'} alt={product.name} />
                                                                    <span className="product-name">{product.name}</span>
                                                                    <button
                                                                        className="add-product-btn"
                                                                        onClick={() => addTrendingProduct(section.id, product)}
                                                                    >
                                                                        <FiPlus /> Add
                                                                    </button>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}

                                                    <div className="trending-products-list">
                                                        {(categoryConfigs[section.id]?.trendingProducts || []).map((product, pIndex) => (
                                                            <div key={product.id} className="trending-product-item">
                                                                <div className="trending-order">
                                                                    <button
                                                                        onClick={() => moveTrendingProduct(section.id, pIndex, 'up')}
                                                                        disabled={pIndex === 0}
                                                                    >
                                                                        <FiChevronUp size={14} />
                                                                    </button>
                                                                    <button
                                                                        onClick={() => moveTrendingProduct(section.id, pIndex, 'down')}
                                                                        disabled={pIndex === (categoryConfigs[section.id]?.trendingProducts?.length || 0) - 1}
                                                                    >
                                                                        <FiChevronDown size={14} />
                                                                    </button>
                                                                </div>
                                                                <img src={product.image || '/placeholder.png'} alt={product.name} />
                                                                <span className="product-name">{product.name}</span>
                                                                <button
                                                                    className="remove-trending-btn"
                                                                    onClick={() => removeTrendingProduct(section.id, product.id)}
                                                                >
                                                                    <FiTrash2 size={14} />
                                                                </button>
                                                            </div>
                                                        ))}
                                                        {(!categoryConfigs[section.id]?.trendingProducts || categoryConfigs[section.id].trendingProducts.length === 0) && (
                                                            <div className="no-trending">
                                                                No trending products configured. Search and add products above.
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="config-actions">
                                                    <button
                                                        className="save-config-btn"
                                                        onClick={() => handleSaveCategoryConfig(section.id)}
                                                        disabled={savingConfig}
                                                    >
                                                        <FiSave /> {savingConfig ? 'Saving...' : 'Save Configuration'}
                                                    </button>
                                                </div>
                                            </>
                                        ) : (
                                            <div className="loading-config">Loading configuration...</div>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <style jsx>{`
                .categories-manager {
                    display: flex;
                    flex-direction: column;
                    gap: 24px;
                }

                .page-header {
                    background: linear-gradient(135deg, #1976d2 0%, #0d47a1 100%);
                    border-radius: 16px;
                    padding: 32px;
                    margin-bottom: 24px;
                    box-shadow: 0 4px 20px rgba(25, 118, 210, 0.25);
                }

                .header-content {
                    display: flex;
                    align-items: center;
                    gap: 20px;
                }

                .header-icon {
                    width: 60px;
                    height: 60px;
                    background: rgba(255, 255, 255, 0.2);
                    border-radius: 16px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                    backdrop-filter: blur(10px);
                }

                .header-text h1 {
                    margin: 0;
                    color: white;
                    font-size: 1.8rem;
                    font-weight: 700;
                    letter-spacing: -0.5px;
                }

                .header-text p {
                    margin: 8px 0 0 0;
                    color: rgba(255, 255, 255, 0.85);
                    font-size: 1rem;
                }

                .add-category-card,
                .categories-list-card {
                    background: white;
                    border-radius: 12px;
                    padding: 24px;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.06);
                }

                .add-category-card h3,
                .categories-list-card h3 {
                    margin: 0 0 16px 0;
                    color: #1a1a1a;
                    font-size: 1.1rem;
                }

                .add-category-form {
                    display: grid;
                    grid-template-columns: 1fr 1fr auto;
                    gap: 16px;
                    align-items: end;
                }

                .form-group {
                    display: flex;
                    flex-direction: column;
                    gap: 6px;
                }

                .form-group label {
                    font-weight: 500;
                    color: #333;
                    font-size: 0.9rem;
                }

                .form-group input {
                    padding: 12px 14px;
                    border: 2px solid #e0e0e0;
                    border-radius: 8px;
                    font-size: 0.95rem;
                    transition: border-color 0.2s;
                }

                .form-group input:focus {
                    outline: none;
                    border-color: #1976d2;
                }

                .form-hint {
                    font-size: 0.8rem;
                    color: #888;
                }

                .add-category-form .add-btn {
                    height: 46px;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 0 24px;
                    background: linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%);
                    color: white;
                    border: none;
                    border-radius: 8px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.2s;
                }

                .add-category-form .add-btn:hover:not(:disabled) {
                    transform: translateY(-1px);
                    box-shadow: 0 4px 12px rgba(46, 125, 50, 0.3);
                }

                .add-category-form .add-btn:disabled {
                    opacity: 0.7;
                    cursor: not-allowed;
                }

                .card-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 16px;
                }

                .save-order-btn {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 10px 20px;
                    background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
                    color: white;
                    border: none;
                    border-radius: 8px;
                    font-weight: 500;
                    cursor: pointer;
                    transition: all 0.2s;
                }

                .save-order-btn:hover:not(:disabled) {
                    transform: translateY(-1px);
                    box-shadow: 0 4px 12px rgba(25, 118, 210, 0.3);
                }

                .save-order-btn:disabled {
                    opacity: 0.7;
                    cursor: not-allowed;
                }

                .categories-list {
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                }

                .category-wrapper {
                    display: flex;
                    flex-direction: column;
                }

                .category-item {
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    padding: 16px;
                    background: #f8f9fa;
                    border-radius: 10px;
                    border: 1px solid #e0e0e0;
                    transition: all 0.2s;
                }

                .category-item:hover {
                    border-color: #1976d2;
                    background: #f5f9ff;
                }

                .category-item.expanded {
                    border-color: #1976d2;
                    background: #e3f2fd;
                    border-radius: 10px 10px 0 0;
                }

                .category-order {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }

                .order-number {
                    width: 32px;
                    height: 32px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: #1976d2;
                    color: white;
                    border-radius: 50%;
                    font-weight: 600;
                    font-size: 0.9rem;
                }

                .order-buttons {
                    display: flex;
                    flex-direction: column;
                    gap: 2px;
                }

                .order-buttons button {
                    width: 28px;
                    height: 22px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: #e3f2fd;
                    border: 1px solid #90caf9;
                    border-radius: 4px;
                    color: #1976d2;
                    cursor: pointer;
                    transition: all 0.2s;
                }

                .order-buttons button:hover:not(:disabled) {
                    background: #1976d2;
                    color: white;
                }

                .order-buttons button:disabled {
                    opacity: 0.3;
                    cursor: not-allowed;
                }

                .category-info {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                    cursor: pointer;
                }

                .category-name {
                    font-weight: 600;
                    color: #1a1a1a;
                    font-size: 1rem;
                }

                .category-id {
                    font-size: 0.85rem;
                    color: #666;
                }

                .expand-hint {
                    display: flex;
                    align-items: center;
                    gap: 4px;
                    font-size: 0.8rem;
                    color: #1976d2;
                    margin-top: 4px;
                }

                .expand-hint svg {
                    transition: transform 0.2s;
                }

                .expand-hint svg.rotated {
                    transform: rotate(90deg);
                }

                .edit-input {
                    padding: 10px 12px;
                    border: 2px solid #1976d2;
                    border-radius: 6px;
                    font-size: 1rem;
                    font-weight: 500;
                    width: 100%;
                    max-width: 300px;
                }

                .category-actions {
                    display: flex;
                    gap: 8px;
                }

                .action-btn {
                    width: 36px;
                    height: 36px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border: 2px solid transparent;
                    border-radius: 10px;
                    cursor: pointer;
                    transition: all 0.2s;
                    text-decoration: none;
                    font-size: 18px;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                }

                .action-btn svg {
                    width: 16px !important;
                    height: 16px !important;
                    min-width: 16px;
                    min-height: 16px;
                }

                .action-btn.trending {
                    background: #fff3e0;
                    color: #e65100;
                    border-color: #ffcc80;
                }

                .action-btn.trending:hover {
                    background: #e65100;
                    color: white;
                    border-color: #e65100;
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(230, 81, 0, 0.3);
                }

                .action-btn.view {
                    background: #e8f5e9;
                    color: #2e7d32;
                    border-color: #a5d6a7;
                }

                .action-btn.view:hover {
                    background: #2e7d32;
                    color: white;
                    border-color: #2e7d32;
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(46, 125, 50, 0.3);
                }

                .action-btn.edit {
                    background: #e3f2fd;
                    color: #1976d2;
                    border-color: #90caf9;
                }

                .action-btn.edit:hover {
                    background: #1976d2;
                    color: white;
                    border-color: #1976d2;
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(25, 118, 210, 0.3);
                }

                .action-btn.delete {
                    background: #ffebee;
                    color: #c62828;
                    border-color: #ef9a9a;
                }

                .action-btn.delete:hover {
                    background: #c62828;
                    color: white;
                    border-color: #c62828;
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(198, 40, 40, 0.3);
                }

                .action-btn.save {
                    background: #e8f5e9;
                    color: #2e7d32;
                    border-color: #a5d6a7;
                }

                .action-btn.save:hover {
                    background: #2e7d32;
                    color: white;
                    border-color: #2e7d32;
                    transform: translateY(-2px);
                }

                .action-btn.cancel {
                    background: #f5f5f5;
                    color: #666;
                    border-color: #bdbdbd;
                }

                .action-btn.cancel:hover {
                    background: #666;
                    color: white;
                    border-color: #666;
                    transform: translateY(-2px);
                }

                /* Config Panel */
                .category-config-panel {
                    background: white;
                    border: 1px solid #1976d2;
                    border-top: none;
                    border-radius: 0 0 10px 10px;
                    padding: 20px;
                }

                .config-section {
                    margin-bottom: 24px;
                }

                .config-section h4 {
                    margin: 0 0 12px 0;
                    font-size: 0.95rem;
                    color: #333;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }

                .config-section .section-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 12px;
                }

                .config-form {
                    display: grid;
                    grid-template-columns: 1fr 2fr;
                    gap: 12px;
                }

                .config-field {
                    display: flex;
                    flex-direction: column;
                    gap: 6px;
                }

                .config-field label {
                    font-size: 0.85rem;
                    font-weight: 500;
                    color: #555;
                }

                .config-field input {
                    padding: 10px 12px;
                    border: 1px solid #ddd;
                    border-radius: 6px;
                    font-size: 0.9rem;
                }

                .config-field input:focus {
                    outline: none;
                    border-color: #1976d2;
                }

                .add-sub-btn {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    padding: 8px 14px;
                    background: #e8f5e9;
                    color: #2e7d32;
                    border: 1px solid #a5d6a7;
                    border-radius: 6px;
                    font-size: 0.85rem;
                    font-weight: 500;
                    cursor: pointer;
                    transition: all 0.2s;
                }

                .add-sub-btn:hover {
                    background: #2e7d32;
                    color: white;
                    border-color: #2e7d32;
                }

                .subcategories-list {
                    display: flex;
                    flex-direction: column;
                    gap: 10px;
                }

                .subcategory-item {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    padding: 12px;
                    background: #f8f9fa;
                    border-radius: 8px;
                    border: 1px solid #e0e0e0;
                }

                .sub-order {
                    display: flex;
                    flex-direction: column;
                    gap: 2px;
                }

                .sub-order button {
                    width: 24px;
                    height: 20px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: #e3f2fd;
                    border: 1px solid #90caf9;
                    border-radius: 4px;
                    color: #1976d2;
                    cursor: pointer;
                    transition: all 0.2s;
                }

                .sub-order button:hover:not(:disabled) {
                    background: #1976d2;
                    color: white;
                }

                .sub-order button:disabled {
                    opacity: 0.3;
                    cursor: not-allowed;
                }

                .sub-fields {
                    flex: 1;
                    display: grid;
                    grid-template-columns: 1fr 2fr 1fr;
                    gap: 10px;
                }

                .sub-fields input {
                    padding: 8px 10px;
                    border: 1px solid #ddd;
                    border-radius: 6px;
                    font-size: 0.85rem;
                }

                .sub-fields input:focus {
                    outline: none;
                    border-color: #1976d2;
                }

                .remove-sub-btn {
                    width: 32px;
                    height: 32px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: #ffebee;
                    color: #c62828;
                    border: 1px solid #ffcdd2;
                    border-radius: 6px;
                    cursor: pointer;
                    transition: all 0.2s;
                }

                .remove-sub-btn:hover {
                    background: #c62828;
                    color: white;
                    border-color: #c62828;
                }

                .no-subcategories {
                    text-align: center;
                    padding: 24px;
                    background: #f8f9fa;
                    border-radius: 8px;
                    color: #666;
                    font-size: 0.9rem;
                }

                .loading-config {
                    text-align: center;
                    padding: 24px;
                    color: #666;
                }

                .config-actions {
                    display: flex;
                    justify-content: flex-end;
                    padding-top: 16px;
                    border-top: 1px solid #e0e0e0;
                }

                .save-config-btn {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 12px 24px;
                    background: linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%);
                    color: white;
                    border: none;
                    border-radius: 8px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.2s;
                }

                .save-config-btn:hover:not(:disabled) {
                    transform: translateY(-1px);
                    box-shadow: 0 4px 12px rgba(46, 125, 50, 0.3);
                }

                .save-config-btn:disabled {
                    opacity: 0.7;
                    cursor: not-allowed;
                }

                /* Trending Products Styles */
                .trending-count {
                    background: #e3f2fd;
                    color: #1976d2;
                    padding: 4px 12px;
                    border-radius: 20px;
                    font-size: 0.85rem;
                    font-weight: 600;
                }

                .trending-search {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    margin-bottom: 12px;
                }

                .trending-search input {
                    flex: 1;
                    padding: 12px 16px;
                    border: 2px solid #e0e0e0;
                    border-radius: 8px;
                    font-size: 0.95rem;
                    transition: border-color 0.2s;
                }

                .trending-search input:focus {
                    outline: none;
                    border-color: #1976d2;
                }

                .trending-search .searching {
                    color: #666;
                    font-size: 0.85rem;
                }

                .search-results {
                    background: #f8f9fa;
                    border: 1px solid #e0e0e0;
                    border-radius: 8px;
                    margin-bottom: 16px;
                    max-height: 200px;
                    overflow-y: auto;
                }

                .search-result-item {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    padding: 10px 14px;
                    border-bottom: 1px solid #e0e0e0;
                    transition: background 0.2s;
                }

                .search-result-item:last-child {
                    border-bottom: none;
                }

                .search-result-item:hover {
                    background: #e3f2fd;
                }

                .search-result-item img {
                    width: 40px;
                    height: 40px;
                    object-fit: cover;
                    border-radius: 6px;
                }

                .search-result-item .product-name {
                    flex: 1;
                    font-size: 0.9rem;
                    color: #333;
                }

                .add-product-btn {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    padding: 6px 12px;
                    background: #e8f5e9;
                    color: #2e7d32;
                    border: 1px solid #a5d6a7;
                    border-radius: 6px;
                    font-size: 0.8rem;
                    cursor: pointer;
                    transition: all 0.2s;
                }

                .add-product-btn:hover {
                    background: #2e7d32;
                    color: white;
                    border-color: #2e7d32;
                }

                .trending-products-list {
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                }

                .trending-product-item {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    padding: 12px;
                    background: #f8f9fa;
                    border: 1px solid #e0e0e0;
                    border-radius: 8px;
                    transition: all 0.2s;
                }

                .trending-product-item:hover {
                    border-color: #1976d2;
                    background: #f5f9ff;
                }

                .trending-order {
                    display: flex;
                    flex-direction: column;
                    gap: 2px;
                }

                .trending-order button {
                    width: 24px;
                    height: 20px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: #e3f2fd;
                    border: 1px solid #90caf9;
                    border-radius: 4px;
                    color: #1976d2;
                    cursor: pointer;
                    transition: all 0.2s;
                }

                .trending-order button:hover:not(:disabled) {
                    background: #1976d2;
                    color: white;
                }

                .trending-order button:disabled {
                    opacity: 0.3;
                    cursor: not-allowed;
                }

                .trending-product-item img {
                    width: 50px;
                    height: 50px;
                    object-fit: cover;
                    border-radius: 8px;
                }

                .trending-product-item .product-name {
                    flex: 1;
                    font-size: 0.9rem;
                    font-weight: 500;
                    color: #333;
                }

                .remove-trending-btn {
                    width: 32px;
                    height: 32px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: #ffebee;
                    color: #c62828;
                    border: 1px solid #ffcdd2;
                    border-radius: 6px;
                    cursor: pointer;
                    transition: all 0.2s;
                }

                .remove-trending-btn:hover {
                    background: #c62828;
                    color: white;
                    border-color: #c62828;
                }

                .no-trending {
                    text-align: center;
                    padding: 24px;
                    background: #f8f9fa;
                    border-radius: 8px;
                    color: #666;
                    font-size: 0.9rem;
                }

                @media (max-width: 768px) {
                    .add-category-form {
                        grid-template-columns: 1fr;
                    }

                    .category-item {
                        flex-wrap: wrap;
                    }

                    .category-info {
                        width: calc(100% - 80px);
                    }

                    .category-actions {
                        width: 100%;
                        justify-content: flex-end;
                        margin-top: 8px;
                    }

                    .config-form {
                        grid-template-columns: 1fr;
                    }

                    .sub-fields {
                        grid-template-columns: 1fr;
                    }
                }
            `}</style>
        </div>
    );
};

export default AdminCategories;
